import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8dfirk13ra8mig8pq0";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后再试。",
        );
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，提交失败，请稍后再试。");
      setStatus("error");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

  return (
    <form
      id="about-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">联系与反馈</h3>
      <p className="mt-1.5 text-sm text-foreground-500">
        如果你有想推荐的作品、想反馈的问题，或希望与我们合作，欢迎留言告诉我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm text-foreground-600">
            你的称呼
          </label>
          <input id="contact-name" name="name" type="text" required placeholder="请输入你的称呼" className={inputClass} />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm text-foreground-600">
            邮箱地址
          </label>
          <input id="contact-email" name="email" type="email" required placeholder="请输入邮箱，方便我们回复" className={inputClass} />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-subject" className="mb-1.5 block text-sm text-foreground-600">
          反馈主题
        </label>
        <input id="contact-subject" name="subject" type="text" placeholder="例如：作品推荐 / 内容纠错 / 合作咨询" className={inputClass} />
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="mb-1.5 block text-sm text-foreground-600">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请填写你的留言，最多 500 字"
          className={`${inputClass} resize-none`}
        ></textarea>
      </div>

      <div className="contact-form-extra-field" aria-hidden="true">
        <label htmlFor="contact-website-alt">网站备用字段</label>
        <input
          id="contact-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          <i className="ri-send-plane-line"></i>
          {status === "submitting" ? "提交中..." : "提交留言"}
        </button>
        {status === "success" && (
          <p className="flex items-center gap-1.5 text-sm text-primary-700">
            <i className="ri-checkbox-circle-line"></i>留言已提交，感谢你的反馈！
          </p>
        )}
        {status === "error" && (
          <p className="flex items-center gap-1.5 text-sm text-secondary-700">
            <i className="ri-error-warning-line"></i>
            {formError}
          </p>
        )}
      </div>
    </form>
  );
}